import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { messagesApi, usersApi, searchApi } from '../../services/api';
import { useNotifications } from '../Notifications/NotificationProvider';
import styles from './MessagesModal.module.css';
import type { SearchUserResult } from '../../types';

type Conversation = { peer: { id: string; username?: string | null; email: string }; last: any; unread: boolean };

export const MessagesModal: React.FC<{
  open: boolean;
  onClose: () => void;
  currentUserId?: string;
  inline?: boolean;
  initialTargetUserId?: string;
  onViewProfile?: (userId: string) => void;
  onActivePeerChange?: (peerId: string | null) => void;
  onBackFromThread?: () => void;
  cameFromProfile?: boolean;
}>
  = ({ open, onClose, currentUserId, inline = false, initialTargetUserId, onViewProfile, onActivePeerChange, onBackFromThread, cameFromProfile = false }) => {
  const { notify } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activePeerId, setActivePeerId] = useState<string | null>(null);
  const [thread, setThread] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [startUsername, setStartUsername] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [checkingBlock, setCheckingBlock] = useState(false);
  const [blocked, setBlocked] = useState<Array<{ blockerId: string; blockedId: string; createdAt: string }>>([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const composerInputRef = useRef<HTMLInputElement | null>(null);
  const conversationsLoadingRef = useRef(false);
  const lastConversationsFetchRef = useRef(0);
  const blocksLoadingRef = useRef(false);
  const lastBlocksFetchRef = useRef(0);
  const markReadRef = useRef<Record<string, number>>({});
  const [compact, setCompact] = useState(false);
  const [showList, setShowList] = useState(true);
  const [showActions, setShowActions] = useState(false);
  const initialTargetHandledRef = useRef<string | null>(null);
  const [userResults, setUserResults] = useState<SearchUserResult[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const userSearchSeqRef = useRef(0);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread.length]);

  // Expose active peer to parent so navigation (e.g. "Back") can return to the correct profile.
  useEffect(() => {
    try { onActivePeerChange?.(activePeerId); } catch { }
  }, [activePeerId, onActivePeerChange]);

  const handleSearchSubmit = useCallback((event?: React.FormEvent) => {
    if (event) event.preventDefault();
    setSearchTerm(searchInput.trim());
  }, [searchInput]);

  const normalizedSearch = useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);

  const threadMatchesSearch = useMemo(() => {
    if (!normalizedSearch || !activePeerId) return false;
    return thread.some((message) => (message?.content || '').toLowerCase().includes(normalizedSearch));
  }, [thread, normalizedSearch, activePeerId]);

  const filteredConversations = useMemo(() => {
    if (!normalizedSearch) return conversations;
    return conversations.filter((conversation) => {
      const peerName = (conversation.peer.username || conversation.peer.email || '').toLowerCase();
      const lastContent = (conversation.last?.content || '').toLowerCase();
      if (peerName.includes(normalizedSearch) || lastContent.includes(normalizedSearch)) {
        return true;
      }
      if (threadMatchesSearch && activePeerId && conversation.peer.id === activePeerId) {
        return true;
      }
      return false;
    });
  }, [conversations, normalizedSearch, threadMatchesSearch, activePeerId]);

  const visibleThread = useMemo(() => {
    if (!normalizedSearch) return thread;
    return thread.filter((message) => (message?.content || '').toLowerCase().includes(normalizedSearch));
  }, [thread, normalizedSearch]);

  const highlightText = useCallback((text: string) => {
    if (!text || !normalizedSearch) return text;
    const lower = text.toLowerCase();
    const parts: Array<string | { value: string }> = [];
    let index = 0;
    let matchIndex = lower.indexOf(normalizedSearch, index);

    while (matchIndex !== -1) {
      if (matchIndex > index) {
        parts.push(text.slice(index, matchIndex));
      }
      parts.push({ value: text.slice(matchIndex, matchIndex + normalizedSearch.length) });
      index = matchIndex + normalizedSearch.length;
      matchIndex = lower.indexOf(normalizedSearch, index);
    }

    if (index < text.length) {
      parts.push(text.slice(index));
    }

    return parts.map((part, idx) => {
      if (typeof part === 'string') {
        return <React.Fragment key={idx}>{part}</React.Fragment>;
      }
      return <mark key={idx}>{part.value}</mark>;
    });
  }, [normalizedSearch]);

  // Responsive: detect compact width based on container (mobile-first approach)
  useEffect(() => {
    const onResize = () => {
      const w = containerRef.current?.clientWidth || window.innerWidth;
      setCompact(w < 768); // Updated to standard tablet breakpoint
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // In compact mode, default to list; switch to thread after selecting
  useEffect(() => {
    if (!compact) return;
    setShowList(!activePeerId);
  }, [compact, activePeerId]);

  // Search users so you can start a new conversation even when you have no existing threads.
  useEffect(() => {
    const term = searchInput.trim();
    if (!term) {
      setUserResults([]);
      setUserSearchLoading(false);
      return;
    }

    const seq = ++userSearchSeqRef.current;
    setUserSearchLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await searchApi.searchGroup(term, 'users', { limit: 8 });
        const items = res.users?.items || [];
        if (userSearchSeqRef.current !== seq) return;
        setUserResults(items.filter((u) => u.id !== currentUserId));
      } catch {
        if (userSearchSeqRef.current !== seq) return;
        setUserResults([]);
      } finally {
        if (userSearchSeqRef.current !== seq) return;
        setUserSearchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(t);
  }, [searchInput, currentUserId]);

  const loadConversations = useCallback(async () => {
    const now = Date.now();
    if (conversationsLoadingRef.current) return;
    if (now - lastConversationsFetchRef.current < 2500) return; // rate-limit rapid calls
    try { setLoading(true);
      conversationsLoadingRef.current = true;
      const data = await messagesApi.conversations();
      setConversations(data.conversations || []);
      lastConversationsFetchRef.current = Date.now();
    } catch (e: any) {
      const message = e?.response?.data?.error || e.message || 'Failed to load conversations';
      notify({ type: 'error', message });
    } finally {
      conversationsLoadingRef.current = false;
      setLoading(false);
    }
  }, [notify]);

  const loadBlocks = useCallback(async () => {
    const now = Date.now();
    if (blocksLoadingRef.current) return;
    if (now - lastBlocksFetchRef.current < 30000) return;
    try {
      setLoadingBlocks(true);
      blocksLoadingRef.current = true;
      const data = await messagesApi.getBlocks();
      setBlocked(data.data || []);
      lastBlocksFetchRef.current = Date.now();
    } catch (e: any) {
      notify({ type: 'error', message: e?.response?.data?.error || e.message || 'Failed to load blocks' });
    } finally {
      setLoadingBlocks(false);
      blocksLoadingRef.current = false;
    }
  }, [notify]);

  const loadThread = useCallback(async (peerId: string) => {
    try {
      setLoading(true);
      const data = await messagesApi.threadWith(peerId);
      setThread(data.messages || []);

      // Mark read (only when needed / not too often)
      const now = Date.now();
      const lastMarked = markReadRef.current[peerId] || 0;
      const conversation = conversations.find(c => c.peer.id === peerId);
      const shouldMark = conversation?.unread || (now - lastMarked > 5000);
      if (shouldMark) {
        try {
          await messagesApi.markReadWith(peerId);
          markReadRef.current[peerId] = Date.now();
        } catch { /* ignore mark read failures */ }
      }

      // Block status: reuse cached blocks when available, otherwise rely on the throttled loadBlocks
      if (blocked.length > 0) {
        setIsBlocked(blocked.some((b: any) => b.blockedId === peerId));
      } else {
        // Trigger a refresh but don't spam the endpoint; loadBlocks is throttled separately
        loadBlocks();
      }
    } catch (e: any) {
      const message = e?.response?.data?.error || e.message || 'Failed to load thread';
      notify({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  }, [blocked, conversations, notify, loadBlocks]);

  useEffect(() => {
    if (!open) return;
    loadConversations();
    loadBlocks();
  }, [open, loadConversations, loadBlocks]);

  useEffect(() => {
    if (!open) {
      initialTargetHandledRef.current = null;
      setSearchInput('');
      setSearchTerm('');
    }
  }, [open]);

  useEffect(() => {
    if (!initialTargetUserId) {
      initialTargetHandledRef.current = null;
    }
  }, [initialTargetUserId]);

  // Function to fetch user info and setup new conversation
  const fetchUserInfoAndSetupConversation = useCallback(async (userId: string) => {
    // Set up the UI immediately so the composer becomes available even if the
    // profile fetch is slow or fails (important in compact/sidebar-only layouts).
    setActivePeerId(userId);
    setThread([]);
    setStartUsername((prev) => prev || userId);

    try {
      const userInfo = await usersApi.getProfile(userId);
      const username = userInfo.username || userInfo.email || userId;
      setStartUsername(username);
    } catch (error) {
      console.warn('Failed to fetch user info for messaging:', error);
      setStartUsername((prev) => prev || userId);
    }
  }, []);

  // Fallback: when navigation opens Messages without threading info in props,
  // pick up a recently requested target from sessionStorage.
  useEffect(() => {
    if (!open || initialTargetUserId) return;
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem('messages:pendingTarget');
    } catch { }
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as { userId?: string; ts?: number };
      const userId = typeof parsed?.userId === 'string' ? parsed.userId : null;
      const ts = typeof parsed?.ts === 'number' ? parsed.ts : 0;
      if (!userId) return;
      if (ts && Date.now() - ts > 10000) return;
      if (compact) setShowList(false);
      fetchUserInfoAndSetupConversation(userId);
    } finally {
      try { sessionStorage.removeItem('messages:pendingTarget'); } catch { }
    }
  }, [open, initialTargetUserId, compact, fetchUserInfoAndSetupConversation]);

  // Handle initial target user selection
  useEffect(() => {
    if (!open || !initialTargetUserId) return;

    // When opening Messages from a profile, immediately show the thread + composer.
    if (compact) setShowList(false);

    const existingConversation = conversations.find(c => c.peer.id === initialTargetUserId);
    initialTargetHandledRef.current = initialTargetUserId;

    if (activePeerId !== initialTargetUserId) {
      setActivePeerId(initialTargetUserId);
    }

    if (existingConversation) {
      loadThread(initialTargetUserId);
    } else {
      fetchUserInfoAndSetupConversation(initialTargetUserId);
    }
  }, [open, initialTargetUserId, conversations, activePeerId, compact, loadThread, fetchUserInfoAndSetupConversation]);

  // Auto-focus the composer when a thread is active.
  useEffect(() => {
    if (!open || !activePeerId) return;
    const t = window.setTimeout(() => composerInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, activePeerId]);

  useEffect(() => {
    if (open && activePeerId) {
      loadThread(activePeerId);
    }
  }, [open, activePeerId, loadThread]);

  const send = async () => {
    const text = input.trim();
    if (!text || !activePeerId) return;
    try {
      await messagesApi.sendTo(activePeerId, text);
      setInput('');
      await loadThread(activePeerId);
      // Only refresh conversations list if this was a new conversation
      const existingConversation = conversations.find(c => c.peer.id === activePeerId);
      if (!existingConversation) {
        await loadConversations();
      }
      notify({ type: 'success', message: 'Message sent', persistent: false });
    } catch (e: any) {
      notify({ type: 'error', message: e?.response?.data?.error || e.message || 'Failed to send' });
    }
  };

  const onExportMine = async () => {
    try {
      const data = await messagesApi.exportMine();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'messages_export.json'; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      notify({ type: 'error', message: e?.response?.data?.error || e.message || 'Failed to export messages' });
    }
  };

  const onDeleteThread = async () => {
    if (!activePeerId) return;
    const ok = window.confirm('Delete this conversation for you? This will remove it from your view.');
    if (!ok) return;
    try {
      await messagesApi.deleteThreadWith(activePeerId);
      notify({ type: 'success', message: 'Conversation removed', persistent: false });
      setThread([]);
      await loadConversations();
    } catch (e: any) {
      notify({ type: 'error', message: e?.response?.data?.error || e.message || 'Failed to delete conversation' });
    }
  };

  const onToggleBlock = async () => {
    if (!activePeerId) return;
    setCheckingBlock(true);
    try {
      if (isBlocked) {
        await messagesApi.unblockUser(activePeerId);
        setIsBlocked(false);
        notify({ type: 'success', message: 'User unblocked', persistent: false });
        loadBlocks();
      } else {
        await messagesApi.blockUser(activePeerId);
        setIsBlocked(true);
        notify({ type: 'success', message: 'User blocked', persistent: false });
        loadBlocks();
      }
    } catch (e: any) {
      notify({ type: 'error', message: e?.response?.data?.error || e.message || 'Failed to update block status' });
    } finally {
      setCheckingBlock(false);
    }
  };

  if (!open && !inline) return null;
  if (inline) {
    return (
      <div ref={containerRef} className={styles.container}>
        <div className={`${styles.mainLayout} ${compact ? styles.mainLayoutCompact : ''}`}>
          <div className={`${styles.sidebar} ${compact && !showList ? styles.sidebarHidden : ''}`}>
            <div className={styles.startConversationSection}>
              <form className={styles.startForm} onSubmit={handleSearchSubmit}>
                <input
                  placeholder="Search in messages"
                  value={searchInput}
                  name="messages-search-inline"
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchInput(value);
                    setSearchTerm(value);
                  }}
                  className={styles.usernameInput}
                  aria-label="Search conversations or messages"
                />
              </form>
            </div>
            <div className={styles.conversationsList}>
              {loading && conversations.length === 0 && <div className={styles.loadingText}>Loading…</div>}
              {searchInput.trim() && (userSearchLoading || userResults.length > 0) && (
                <div className={styles.peopleSection}>
                  <div className={styles.peopleTitle}>People</div>
                  {userSearchLoading && <div className={styles.loadingText}>Searching users…</div>}
                  {!userSearchLoading && userResults.map((u) => {
                    const label = u.username || u.id;
                    return (
                      <button
                        key={u.id}
                        onClick={() => {
                          fetchUserInfoAndSetupConversation(u.id);
                          if (compact) setShowList(false);
                        }}
                        className={styles.userResultItem}
                        title={`Message ${label}`}
                      >
                        <div className={styles.userResultName}>{label}</div>
                        <div className={styles.userResultMeta}>{u.role} • rep {u.reputation}</div>
                      </button>
                    );
                  })}
                </div>
              )}
              {filteredConversations.map(c => (
                <button
                  key={c.peer.id}
                  onClick={()=>{ setActivePeerId(c.peer.id); if (compact) setShowList(false); }}
                  className={styles.conversationItem}
                >
                  <div className={styles.conversationHeader}>
                    <div className={styles.conversationUser}>{highlightText(c.peer.username || c.peer.email)}</div>
                    {c.unread && <span className={styles.unreadBadge}>New</span>}
                  </div>
                  <div className={styles.conversationPreview}>{highlightText(c.last?.content || '')}</div>
                </button>
              ))}
              {filteredConversations.length === 0 && !loading && normalizedSearch && (
                <div className={styles.emptyState}>No conversations match your search.</div>
              )}
              {filteredConversations.length === 0 && !loading && !normalizedSearch && (
                <div className={styles.emptyState}>No conversations yet.</div>
              )}
              {conversations.length === 0 && !loading && !normalizedSearch && (
                <div className={styles.emptyState}>Search for a user above to start a new chat.</div>
              )}
              <div className={styles.blockedSection}>
                <div className={styles.blockedTitle}>Blocked Users</div>
                {loadingBlocks && <div className={styles.loadingText}>Loading…</div>}
                {!loadingBlocks && blocked.length === 0 && (
                  <div className={styles.emptyState}>No blocked users.</div>
                )}
                <div className={styles.blockedList}>
                  {blocked.map(b => (
                    <div key={`${b.blockerId}:${b.blockedId}`} className={styles.blockedItem}>
                      <div className={styles.blockedUser}>{b.blockedId}</div>
                      <button
                        className={styles.unblockButton}
                        onClick={async ()=>{
                          try {
                            await messagesApi.unblockUser(b.blockedId);
                            notify({ type:'success', message: 'Unblocked', persistent: false });
                            loadBlocks();
                          } catch (e:any) {
                            notify({ type:'error', message: e?.response?.data?.error || e.message || 'Failed' });
                          }
                        }}
                      >
                        Unblock
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className={`${styles.threadArea} ${compact && showList ? styles.threadAreaHidden : ''}`}>
            <div className={styles.threadToolbar}>
              <div className={styles.threadInfo}>
                {compact && !showList && (
                  <button
                    className={styles.backButton}
                    onClick={() => {
                      // If we came from a profile, go back to that profile
                      if (cameFromProfile && onBackFromThread) {
                        onBackFromThread();
                      } else {
                        setShowList(true);
                      }
                    }}
                    title={cameFromProfile && onBackFromThread ? "Back" : "Mailbox"}
                    aria-label={cameFromProfile && onBackFromThread ? "Back" : "Mailbox"}
                  >
                    {cameFromProfile && onBackFromThread ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M19 12H5" />
                        <path d="M12 19l-7-7 7-7" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M4 7h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
                        <path d="M22 9.5 12 15 2 9.5" />
                      </svg>
                    )}
                  </button>
                )}
                <div className={styles.threadTitle}>
                  {activePeerId ? (() => {
                    const existingConv = conversations.find(c => c.peer.id === activePeerId);
                    const displayName = existingConv?.peer?.username
                      || existingConv?.peer?.email
                      || startUsername
                      || activePeerId;
                    const label = existingConv ? 'Chat with ' : startUsername ? 'New conversation with ' : 'Chat with ';
                    return (
                      <span>
                        {label}
                        {onViewProfile ? (
                          <button
                            type="button"
                            className={styles.profileLinkButton}
                            onClick={() => onViewProfile(activePeerId)}
                            title="View profile"
                          >
                            {displayName}
                          </button>
                        ) : (
                          displayName
                        )}
                      </span>
                    );
                  })() : 'No conversation selected'}
                </div>
              </div>
              <div className={styles.threadActions}>
                {compact ? (
                  <>
                    <button
                      className={styles.moreButton}
                      onClick={(e)=>{ e.stopPropagation(); setShowActions(v=>!v); }}
                      title="More actions"
                    >
                      ⋯
                    </button>
                    {showActions && (
                      <div className={styles.actionsDropdown} onClick={(e)=>e.stopPropagation()}>
                        <button className={styles.dropdownButton} onClick={()=>{ setShowActions(false); onExportMine(); }}>Export</button>
                        <button className={styles.dropdownButton} onClick={()=>{ setShowActions(false); onDeleteThread(); }} disabled={!activePeerId}>Delete</button>
                        <button className={styles.dropdownButton} onClick={()=>{ setShowActions(false); onToggleBlock(); }} disabled={!activePeerId || checkingBlock}>
                          {isBlocked ? 'Unblock' : 'Block'}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <button className={styles.actionButton} onClick={onExportMine} title="Export my messages">Export</button>
                    <button className={styles.actionButton} onClick={onDeleteThread} disabled={!activePeerId} title="Remove this conversation from my view">Delete</button>
                    <button className={styles.actionButton} onClick={onToggleBlock} disabled={!activePeerId || checkingBlock} title={isBlocked ? 'Unblock this user' : 'Block this user'}>
                      {isBlocked ? 'Unblock' : 'Block'}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className={styles.messagesArea}>
              {activePeerId ? (
                visibleThread.length > 0 ? (
                  visibleThread.map(m => (
                    <div key={m.id} className={`${styles.messageItem} ${m.senderId === currentUserId ? styles.messageItemSent : styles.messageItemReceived}`}>
                      <div className={`${styles.messageBubble} ${m.senderId === currentUserId ? styles.messageBubbleSent : styles.messageBubbleReceived}`}>
                        <div className={styles.messageContent}>{highlightText(m.content || '')}</div>
                        <div className={styles.messageTime}>{new Date(m.createdAt).toLocaleString()}</div>
                      </div>
                    </div>
                  ))
                ) : normalizedSearch ? (
                  <div className={styles.emptyThread}>No messages match your search.</div>
                ) : (
                  <div className={styles.emptyThread}>
                    {startUsername ? `Start a conversation with ${startUsername}` : 'No messages yet. Type a message below to start the conversation.'}
                  </div>
                )
              ) : (
                <div className={styles.noConversation}>Pick a conversation to view messages.</div>
              )}
              <div ref={bottomRef} />
            </div>
            <div className={styles.composeArea}>
              <input
                ref={composerInputRef}
                placeholder={activePeerId ? 'Type a message…' : 'Select a conversation to view messages'}
                value={input}
                name="message-compose-inline"
                onChange={(e)=>setInput(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                className={styles.messageInput}
                disabled={!activePeerId}
              />
              <button className={styles.sendButton} onClick={send} disabled={!activePeerId || !input.trim()}>Send</button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Default modal rendering
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div ref={containerRef} className={styles.modalContent} onClick={(e)=>e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Messages</h2>
          <button onClick={onClose} className={styles.closeButton}>×</button>
        </div>
        <div className={styles.mainLayout}>
          <div className={styles.sidebar}>
            <div className={styles.startConversationSection}>
              <form className={styles.startForm} onSubmit={handleSearchSubmit}>
                <input
                  placeholder="Search in messages"
                  value={searchInput}
                  name="messages-search-modal"
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchInput(value);
                    setSearchTerm(value);
                  }}
                  className={styles.usernameInput}
                  aria-label="Search conversations or messages"
                />
              </form>
            </div>
            <div className={styles.conversationsList}>
              {loading && conversations.length === 0 && <div className={styles.loadingText}>Loading…</div>}
              {searchInput.trim() && (userSearchLoading || userResults.length > 0) && (
                <div className={styles.peopleSection}>
                  <div className={styles.peopleTitle}>People</div>
                  {userSearchLoading && <div className={styles.loadingText}>Searching users…</div>}
                  {!userSearchLoading && userResults.map((u) => {
                    const label = u.username || u.id;
                    return (
                      <button
                        key={u.id}
                        onClick={() => fetchUserInfoAndSetupConversation(u.id)}
                        className={styles.userResultItem}
                        title={`Message ${label}`}
                      >
                        <div className={styles.userResultName}>{label}</div>
                        <div className={styles.userResultMeta}>{u.role} • rep {u.reputation}</div>
                      </button>
                    );
                  })}
                </div>
              )}
              {filteredConversations.map(c => (
                <button
                  key={c.peer.id}
                  onClick={()=>setActivePeerId(c.peer.id)}
                  className={styles.conversationItem}
                >
                  <div className={styles.conversationHeader}>
                    <div className={styles.conversationUser}>{highlightText(c.peer.username || c.peer.email)}</div>
                    {c.unread && <span className={styles.unreadBadge}>New</span>}
                  </div>
                  <div className={styles.conversationPreview}>{highlightText(c.last?.content || '')}</div>
                </button>
              ))}
              {filteredConversations.length === 0 && !loading && normalizedSearch && (
                <div className={styles.emptyState}>No conversations match your search.</div>
              )}
              {filteredConversations.length === 0 && !loading && !normalizedSearch && (
                <div className={styles.emptyState}>No conversations yet.</div>
              )}
              {conversations.length === 0 && !loading && !normalizedSearch && (
                <div className={styles.emptyState}>Search for a user above to start a new chat.</div>
              )}
              <div className={styles.blockedSection}>
                <div className={styles.blockedTitle}>Blocked Users</div>
                {loadingBlocks && <div className={styles.loadingText}>Loading…</div>}
                {!loadingBlocks && blocked.length === 0 && (
                  <div className={styles.emptyState}>No blocked users.</div>
                )}
                <div className={styles.blockedList}>
                  {blocked.map(b => (
                    <div key={`${b.blockerId}:${b.blockedId}`} className={styles.blockedItem}>
                      <div className={styles.blockedUser}>{b.blockedId}</div>
                      <button
                        className={styles.unblockButton}
                        onClick={async ()=>{
                          try {
                            await messagesApi.unblockUser(b.blockedId);
                            notify({ type:'success', message: 'Unblocked', persistent: false });
                            loadBlocks();
                          } catch (e:any) {
                            notify({ type:'error', message: e?.response?.data?.error || e.message || 'Failed' });
                          }
                        }}
                      >
                        Unblock
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.threadArea}>
            <div className={styles.threadToolbar}>
              <div className={styles.threadInfo}>
                <div className={styles.threadTitle}>
                  {activePeerId ? (() => {
                    const existingConv = conversations.find(c => c.peer.id === activePeerId);
                    const displayName = existingConv?.peer?.username
                      || existingConv?.peer?.email
                      || startUsername
                      || activePeerId;
                    const label = existingConv ? 'Chat with ' : startUsername ? 'New conversation with ' : 'Chat with ';
                    return (
                      <span>
                        {label}
                        {onViewProfile ? (
                          <button
                            type="button"
                            className={styles.profileLinkButton}
                            onClick={() => onViewProfile(activePeerId)}
                            title="View profile"
                          >
                            {displayName}
                          </button>
                        ) : (
                          displayName
                        )}
                      </span>
                    );
                  })() : 'No conversation selected'}
                </div>
              </div>
              <div className={styles.threadActions}>
                <button className={styles.actionButton} onClick={onExportMine} title="Export my messages">Export</button>
                <button className={styles.actionButton} onClick={onDeleteThread} disabled={!activePeerId} title="Remove this conversation from my view">Delete</button>
                <button className={styles.actionButton} onClick={onToggleBlock} disabled={!activePeerId || checkingBlock} title={isBlocked ? 'Unblock this user' : 'Block this user'}>
                  {isBlocked ? 'Unblock' : 'Block'}
                </button>
              </div>
            </div>
            <div className={styles.messagesArea}>
              {activePeerId ? (
                visibleThread.length > 0 ? (
                  visibleThread.map(m => (
                    <div key={m.id} className={`${styles.messageItem} ${m.senderId === currentUserId ? styles.messageItemSent : styles.messageItemReceived}`}>
                      <div className={`${styles.messageBubble} ${m.senderId === currentUserId ? styles.messageBubbleSent : styles.messageBubbleReceived}`}>
                        <div className={styles.messageContent}>{highlightText(m.content || '')}</div>
                        <div className={styles.messageTime}>{new Date(m.createdAt).toLocaleString()}</div>
                      </div>
                    </div>
                  ))
                ) : normalizedSearch ? (
                  <div className={styles.emptyThread}>No messages match your search.</div>
                ) : (
                  <div className={styles.emptyThread}>
                    {startUsername ? `Start a conversation with ${startUsername}` : 'No messages yet. Type a message below to start the conversation.'}
                  </div>
                )
              ) : (
                <div className={styles.noConversation}>Pick a conversation to view messages.</div>
              )}
              <div ref={bottomRef} />
            </div>
            <div className={styles.composeArea}>
              <input
                ref={composerInputRef}
                placeholder={activePeerId ? 'Type a message…' : 'Select a conversation to view messages'}
                value={input}
                name="message-compose-modal"
                onChange={(e)=>setInput(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                className={styles.messageInput}
                disabled={!activePeerId}
              />
              <button className={styles.sendButton} onClick={send} disabled={!activePeerId || !input.trim()}>Send</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesModal;
